import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Search, 
  Filter, 
  Download,
  Eye,
  Mail,
  Calendar,
  DollarSign,
  Users,
  CheckCircle,
  RefreshCw,
  Edit,
  X,
  FileText,
  Trash2,
  Square,
  CheckSquare,
  UserPlus,
  Ticket
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Email Preview Modal Component
const EmailPreviewModal = ({ booking, landingPages, onClose }) => {
  const ticketNumber = booking?.id?.slice(0, 8).toUpperCase() || "TKT-0000";
  
  // Find the landing page to get actual event details
  const landingPage = landingPages?.find(p => p.slug === booking?.page_slug);
  const pageData = landingPage?.page_data || landingPage || {};
  
  // Format date nicely
  const formatEventDate = (dateStr) => {
    if (!dateStr) return "TBD";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };
  
  const eventName = "SUDS ON THE VINE"; // Main event name
  const eventDate = formatEventDate(pageData.date);
  const eventTime = pageData.time || "6:30 PM";
  const eventLocation = pageData.location || "Blue Quill Community League";
  const eventAddress = "11304 25 Ave NW, Edmonton, AB";
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-6 border-b border-[rgba(212,175,55,0.15)]">
          <h2 className="text-xl font-['Playfair_Display'] font-bold text-[#F5F5F0]">
            Email Sent to Guest
          </h2>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-[#F5F5F0]">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Email Header Info */}
          <div className="mb-4 p-4 bg-[#121212] border border-[rgba(212,175,55,0.15)] rounded">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-[#A1A1AA]">To:</span> <span className="text-[#F5F5F0]">{booking?.email}</span></div>
              <div><span className="text-[#A1A1AA]">From:</span> <span className="text-[#F5F5F0]">noreply@send.mixandmingleevents.com</span></div>
              <div className="col-span-2"><span className="text-[#A1A1AA]">Subject:</span> <span className="text-[#D4AF37]">🎟️ TICKET #{ticketNumber} - {eventName}</span></div>
            </div>
          </div>

          {/* Email Body Preview */}
          <div className="bg-[#0F0F0F] border border-[#333] rounded overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#D4AF37] to-[#B8860B] p-6 text-center">
              <h1 className="text-[#050505] text-2xl font-bold">🎟️ YOUR EVENT TICKET</h1>
              <p className="text-[#050505] mt-2">Present this email at the door</p>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              <p className="text-lg text-[#F5F5F0]">Hi <strong>{booking?.name}</strong>,</p>
              <p className="text-[#F5F5F0]">Your booking is <strong className="text-green-500">CONFIRMED</strong>! This email is your ticket to the event.</p>

              {/* Ticket Box */}
              <div className="bg-[#1a1a1a] border-2 border-[#D4AF37] p-6 text-center rounded">
                <h2 className="text-[#D4AF37] text-xl font-bold mb-2">{eventName}</h2>
                <p className="text-[#888] text-sm">TICKET NUMBER</p>
                <p className="text-[#D4AF37] text-3xl font-mono font-bold tracking-widest my-2">{ticketNumber}</p>
              </div>

              {/* Event Details */}
              <div className="bg-[#1a1a1a] border border-[#333] p-4 rounded">
                <h3 className="text-[#D4AF37] font-bold mb-3">📋 Event Details</h3>
                <table className="w-full text-sm">
                  <tbody>
                    <tr><td className="py-1 text-[#888] w-32">Event:</td><td className="text-[#F5F5F0] font-bold">{eventName}</td></tr>
                    <tr><td className="py-1 text-[#888]">Date:</td><td className="text-[#F5F5F0]">{eventDate}</td></tr>
                    <tr><td className="py-1 text-[#888]">Time:</td><td className="text-[#F5F5F0]">{eventTime}</td></tr>
                    <tr><td className="py-1 text-[#888]">Location:</td><td className="text-[#F5F5F0]">{eventLocation}</td></tr>
                    <tr><td className="py-1 text-[#888]">Address:</td><td className="text-[#F5F5F0]">{eventAddress}</td></tr>
                  </tbody>
                </table>
              </div>

              {/* Ticket Info */}
              <div className="bg-[#1a1a1a] border border-[#333] p-4 rounded">
                <h3 className="text-[#D4AF37] font-bold mb-3">🎫 Ticket Information</h3>
                <table className="w-full text-sm">
                  <tbody>
                    <tr><td className="py-1 text-[#888] w-32">Ticket Type:</td><td className="text-[#F5F5F0] font-bold">{booking?.ticket_type}</td></tr>
                    <tr><td className="py-1 text-[#888]">Quantity:</td><td className="text-[#F5F5F0]">{booking?.quantity} ticket(s)</td></tr>
                    <tr><td className="py-1 text-[#888]">Guest Name:</td><td className="text-[#F5F5F0]">{booking?.name}</td></tr>
                    <tr><td className="py-1 text-[#888]">Total Paid:</td><td className="text-green-500 font-bold">C${booking?.total_amount?.toFixed(2)}</td></tr>
                  </tbody>
                </table>
              </div>

              {/* What to Bring */}
              <div className="bg-[#1a1a1a] border-l-4 border-[#D4AF37] p-4">
                <h4 className="text-[#D4AF37] font-bold mb-2">📱 What to Bring</h4>
                <ul className="list-disc list-inside text-[#F5F5F0] text-sm space-y-1">
                  <li>This confirmation email (show on your phone or print)</li>
                  <li>Valid photo ID matching the name above</li>
                  <li>Your enthusiasm! 🎊</li>
                </ul>
              </div>

              <p className="text-[#F5F5F0]">We can't wait to see you there!</p>
              <p className="text-[#F5F5F0]">Cheers,<br/><strong className="text-[#D4AF37]">The Mix & Mingle Team</strong></p>
            </div>

            {/* Footer */}
            <div className="bg-[#1a1a1a] p-4 text-center border-t border-[#333]">
              <p className="text-[#666] text-xs">Questions? Contact us at hello@mixandmingleevents.com</p>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-[rgba(212,175,55,0.15)]">
          <button onClick={onClose} className="btn-primary w-full">Close Preview</button>
        </div>
      </motion.div>
    </div>
  );
};

// Edit Booking Modal Component
const EditBookingModal = ({ booking, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: booking?.name || "",
    email: booking?.email || "",
    phone: booking?.phone || "",
    ticket_type: booking?.ticket_type || "",
    quantity: booking?.quantity || 1,
    total_amount: booking?.total_amount || 0,
    payment_status: booking?.payment_status || "pending",
    notes: booking?.notes || ""
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/landing-page-bookings/${booking.id}`, formData);
      toast.success("Booking updated successfully");
      onSave();
      onClose();
    } catch (error) {
      toast.error("Failed to update booking");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-6 border-b border-[rgba(212,175,55,0.15)]">
          <h2 className="text-xl font-['Playfair_Display'] font-bold text-[#F5F5F0]">
            Edit Booking
          </h2>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-[#F5F5F0]">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Customer Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1">Ticket Type</label>
              <input
                type="text"
                value={formData.ticket_type}
                onChange={(e) => setFormData({ ...formData, ticket_type: e.target.value })}
                className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1">Quantity</label>
              <input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1">Total Amount (C$)</label>
              <input
                type="number"
                step="0.01"
                value={formData.total_amount}
                onChange={(e) => setFormData({ ...formData, total_amount: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1">Payment Status</label>
              <select
                value={formData.payment_status}
                onChange={(e) => setFormData({ ...formData, payment_status: e.target.value })}
                className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="cancelled">Cancelled</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none resize-none"
              placeholder="Add any notes about this booking..."
            />
          </div>

          <div className="flex gap-4 pt-4 border-t border-[rgba(212,175,55,0.15)]">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex-1"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Add Manual Booking Modal Component
const AddManualBookingModal = ({ mainEvents, landingPages, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    ticket_type: "Manual Entry",
    quantity: 1,
    total_amount: 0,
    notes: "",
    landing_page_slug: ""
  });
  const [saving, setSaving] = useState(false);
  const [generatedCode, setGeneratedCode] = useState(null);

  // Find the first main event that has landing pages
  const defaultEventId = mainEvents?.[0]?.id || "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/admin/main-events/${defaultEventId}/attendees`,
        {
          ...formData,
          main_event_id: defaultEventId
        }
      );
      setGeneratedCode(response.data);
      toast.success(`Manual booking created! Code: ${response.data.booking_code}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setSaving(false);
    }
  };

  if (generatedCode) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] w-full max-w-md"
        >
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-900/30 rounded-full flex items-center justify-center">
              <Ticket className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-2" data-testid="manual-booking-success-title">
              Booking Created!
            </h2>
            <p className="text-[#A1A1AA] mb-6">Manual booking has been generated</p>
            
            <div className="bg-[#121212] border border-[rgba(212,175,55,0.3)] p-6 mb-6">
              <p className="text-[#A1A1AA] text-sm mb-1">Booking Code</p>
              <p className="text-3xl font-mono font-bold text-[#D4AF37]" data-testid="generated-booking-code">
                {generatedCode.booking_code}
              </p>
              <p className="text-[#A1A1AA] text-xs mt-2">Guest: {formData.name}</p>
              <p className="text-[#A1A1AA] text-xs">Source: {landingPages?.find(lp => lp.slug === formData.landing_page_slug)?.slug?.toUpperCase().replace(/-/g, ' ') || 'Manual'}</p>
            </div>

            <button
              onClick={() => { onSave(); onClose(); }}
              className="btn-primary w-full"
              data-testid="manual-booking-done-btn"
            >
              Done
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-6 border-b border-[rgba(212,175,55,0.15)]">
          <div>
            <h2 className="text-xl font-['Playfair_Display'] font-bold text-[#F5F5F0]" data-testid="add-manual-booking-title">
              Add Manual Booking
            </h2>
            <p className="text-[#A1A1AA] text-sm mt-1">Generate a ticket for a guest manually</p>
          </div>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-[#F5F5F0]">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Guest Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              placeholder="Full name"
              data-testid="manual-booking-name"
            />
          </div>
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Email *</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              placeholder="Email address"
              data-testid="manual-booking-email"
            />
          </div>
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              placeholder="Phone number"
            />
          </div>
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Landing Page Source</label>
            <select
              value={formData.landing_page_slug}
              onChange={(e) => setFormData({ ...formData, landing_page_slug: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              data-testid="manual-booking-source"
            >
              <option value="">-- Select Source --</option>
              {landingPages?.map(lp => (
                <option key={lp.slug} value={lp.slug}>
                  {lp.slug.toUpperCase().replace(/-/g, ' ')} ({lp.slug})
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1">Ticket Type</label>
              <input
                type="text"
                value={formData.ticket_type}
                onChange={(e) => setFormData({ ...formData, ticket_type: e.target.value })}
                className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1">Quantity</label>
              <input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Amount Paid (C$)</label>
            <input
              type="number"
              step="0.01"
              value={formData.total_amount}
              onChange={(e) => setFormData({ ...formData, total_amount: parseFloat(e.target.value) || 0 })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none resize-none"
              placeholder="Optional notes..."
            />
          </div>

          <div className="flex gap-4 pt-4 border-t border-[rgba(212,175,55,0.15)]">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="manual-booking-submit">
              {saving ? "Creating..." : "Create Booking & Generate Ticket"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

const AdminBookingsPage = () => {
  const [events, setEvents] = useState([]);
  const [eventBookings, setEventBookings] = useState([]);
  const [experienceBookings, setExperienceBookings] = useState([]);
  const [landingPageBookings, setLandingPageBookings] = useState([]);
  const [landingPages, setLandingPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("landing");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedEvent, setSelectedEvent] = useState("all");
  const [selectedLandingPage, setSelectedLandingPage] = useState("all");
  const [editingBooking, setEditingBooking] = useState(null);
  const [viewingEmail, setViewingEmail] = useState(null);
  const [showAddManual, setShowAddManual] = useState(false);
  const [mainEvents, setMainEvents] = useState([]);
  
  // Bulk selection state
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Clear selection when changing tabs
  useEffect(() => {
    setSelectedBookings([]);
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const [eventRes, expRes, eventsListRes, landingRes, landingPagesRes, mainEventsRes] = await Promise.all([
        axios.get(`${API}/admin/event-bookings`),
        axios.get(`${API}/admin/experience-bookings`),
        axios.get(`${API}/admin/events`),
        axios.get(`${API}/admin/landing-page-bookings`),
        axios.get(`${API}/admin/event-landing-pages`),
        axios.get(`${API}/admin/main-events`)
      ]);
      setEventBookings(eventRes.data);
      setExperienceBookings(expRes.data);
      setEvents(eventsListRes.data);
      setLandingPageBookings(landingRes.data);
      setLandingPages(landingPagesRes.data);
      setMainEvents(mainEventsRes.data);
      setSelectedBookings([]);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const confirmBooking = async (bookingId) => {
    try {
      await axios.put(`${API}/admin/landing-page-bookings/${bookingId}/confirm?should_send_email=true`);
      toast.success("Booking confirmed and email sent!");
      fetchData();
    } catch (error) {
      toast.error("Failed to confirm booking");
      console.error(error);
    }
  };

  const resendEmail = async (bookingId) => {
    try {
      await axios.post(`${API}/admin/landing-page-bookings/${bookingId}/resend-email`);
      toast.success("Confirmation email resent!");
    } catch (error) {
      toast.error("Failed to resend email");
      console.error(error);
    }
  };

  const [deletingBooking, setDeletingBooking] = useState(null);

  const deleteBooking = async (bookingId) => {
    try {
      await axios.delete(`${API}/admin/landing-page-bookings/${bookingId}`);
      toast.success("Booking deleted successfully");
      setDeletingBooking(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete booking");
      console.error(error);
    }
  };

  const currentBookings = activeTab === "events" 
    ? eventBookings 
    : activeTab === "experiences" 
      ? experienceBookings 
      : landingPageBookings;
  
  const filteredBookings = currentBookings.filter(booking => {
    const matchesSearch = 
      booking.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.event_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.page_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.package_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.phone?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || booking.payment_status === statusFilter;
    
    const matchesEvent = selectedEvent === "all" || booking.event_id === selectedEvent;
    
    // Landing page filter
    const matchesLandingPage = selectedLandingPage === "all" || booking.page_slug === selectedLandingPage;
    
    return matchesSearch && matchesStatus && matchesEvent && matchesLandingPage;
  });

  // Bulk selection functions
  const toggleBookingSelection = (bookingId) => {
    setSelectedBookings(prev => 
      prev.includes(bookingId) 
        ? prev.filter(id => id !== bookingId)
        : [...prev, bookingId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedBookings.length === filteredBookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(filteredBookings.map(b => b.id));
    }
  };

  const bulkDeleteBookings = async () => {
    if (selectedBookings.length === 0) return;
    setBulkDeleting(true);
    try {
      const response = await axios.post(`${API}/admin/landing-page-bookings/bulk-delete`, selectedBookings);
      toast.success(`Deleted ${response.data.deleted_count} bookings`);
      setSelectedBookings([]);
      setShowBulkDelete(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete bookings");
      console.error(error);
    } finally {
      setBulkDeleting(false);
    }
  };

  // Get unique landing page events for filter dropdown - from actual landing pages
  const uniqueLandingPageEvents = landingPages.map(p => ({
    slug: p.slug,
    title: p.slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  }));

  const [syncing, setSyncing] = useState(false);

  const syncStripePayments = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/admin/sync-stripe-payments`);
      const { checked, updated, errors } = response.data;
      if (updated > 0) {
        toast.success(`Synced ${updated} payments from Stripe!`);
        fetchData();
      } else if (checked > 0) {
        toast.info(`Checked ${checked} bookings - no new payments found`);
      } else {
        toast.info("No pending bookings to check");
      }
      if (errors?.length > 0) {
        toast.error(`${errors.length} bookings had errors`);
      }
    } catch (error) {
      toast.error("Failed to sync with Stripe");
      console.error(error);
    } finally {
      setSyncing(false);
    }
  };

  // Group bookings by event for summary
  const bookingsByEvent = eventBookings.reduce((acc, booking) => {
    const eventId = booking.event_id || 'unknown';
    if (!acc[eventId]) {
      acc[eventId] = {
        event_title: booking.event_title || eventId,
        count: 0,
        revenue: 0,
        guests: 0
      };
    }
    acc[eventId].count++;
    if (booking.payment_status === 'paid') {
      acc[eventId].revenue += booking.total_amount || 0;
      acc[eventId].guests += booking.quantity || 1;
    }
    return acc;
  }, {});

  const totalRevenue = filteredBookings
    .filter(b => b.payment_status === "paid")
    .reduce((sum, b) => sum + (b.total_amount || 0), 0);

  const totalGuests = filteredBookings
    .filter(b => b.payment_status === "paid")
    .reduce((sum, b) => sum + (b.quantity || b.guest_count || 1), 0);

  const handleExport = () => {
    window.open(`${API}/admin/export/bookings`, '_blank');
  };

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-bookings">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-2">
              Bookings
            </h1>
            <p className="text-[#A1A1AA]">Manage all confirmed bookings (Stripe-confirmed payments only)</p>
          </div>
          <div className="flex gap-3">
            {activeTab === "landing" && selectedBookings.length > 0 && (
              <button
                onClick={() => setShowBulkDelete(true)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium flex items-center gap-2 transition-colors"
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-5 h-5" />
                Delete ({selectedBookings.length})
              </button>
            )}
            <button
              onClick={() => setShowAddManual(true)}
              className="btn-secondary flex items-center gap-2"
              data-testid="add-manual-booking-btn"
            >
              <UserPlus className="w-5 h-5" />
              Add Guest Manually
            </button>
            <button
              onClick={syncStripePayments}
              disabled={syncing}
              className="btn-primary flex items-center gap-2"
              data-testid="sync-stripe-btn"
            >
              <RefreshCw className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Stripe'}
            </button>
            <button
              onClick={handleExport}
              className="btn-secondary flex items-center gap-2"
              data-testid="export-bookings-btn"
            >
              <Download className="w-5 h-5" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 border-b border-[rgba(212,175,55,0.15)] overflow-x-auto">
          <button
            onClick={() => setActiveTab("landing")}
            className={`pb-4 px-2 text-sm uppercase tracking-wider transition-colors relative whitespace-nowrap ${
              activeTab === "landing" 
                ? "text-[#D4AF37]" 
                : "text-[#A1A1AA] hover:text-[#F5F5F0]"
            }`}
          >
            Landing Page Bookings
            <span className="ml-2 px-2 py-0.5 bg-[rgba(212,175,55,0.2)] text-[#D4AF37] text-xs">
              {landingPageBookings.length}
            </span>
            {activeTab === "landing" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37]" />
            )}
          </button>
          <button
            onClick={() => setActiveTab("events")}
            className={`pb-4 px-2 text-sm uppercase tracking-wider transition-colors relative whitespace-nowrap ${
              activeTab === "events" 
                ? "text-[#D4AF37]" 
                : "text-[#A1A1AA] hover:text-[#F5F5F0]"
            }`}
          >
            Event Bookings
            <span className="ml-2 px-2 py-0.5 bg-[rgba(212,175,55,0.2)] text-[#D4AF37] text-xs">
              {eventBookings.length}
            </span>
            {activeTab === "events" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37]" />
            )}
          </button>
          <button
            onClick={() => setActiveTab("experiences")}
            className={`pb-4 px-2 text-sm uppercase tracking-wider transition-colors relative whitespace-nowrap ${
              activeTab === "experiences" 
                ? "text-[#D4AF37]" 
                : "text-[#A1A1AA] hover:text-[#F5F5F0]"
            }`}
          >
            Experience Bookings
            <span className="ml-2 px-2 py-0.5 bg-[rgba(212,175,55,0.2)] text-[#D4AF37] text-xs">
              {experienceBookings.length}
            </span>
            {activeTab === "experiences" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37]" />
            )}
          </button>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-6">
          <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-[#A1A1AA] text-sm">Total Revenue</span>
            </div>
            <p className="text-2xl font-['Playfair_Display'] font-bold text-[#F5F5F0]">
              C${totalRevenue.toLocaleString()}
            </p>
          </div>
          <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-[#A1A1AA] text-sm">Total Guests</span>
            </div>
            <p className="text-2xl font-['Playfair_Display'] font-bold text-[#F5F5F0]">
              {totalGuests}
            </p>
          </div>
          <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-[#A1A1AA] text-sm">Total Bookings</span>
            </div>
            <p className="text-2xl font-['Playfair_Display'] font-bold text-[#F5F5F0]">
              {filteredBookings.length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#a1a1aa]" />
            <input
              type="text"
              placeholder="Search by name, email, or event..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 pl-10 focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          {activeTab === "landing" && (
            <select
              value={selectedLandingPage}
              onChange={(e) => setSelectedLandingPage(e.target.value)}
              className="bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none min-w-[200px]"
              data-testid="landing-page-filter"
            >
              <option value="all">All Events</option>
              {uniqueLandingPageEvents.map(page => (
                <option key={page.slug} value={page.slug}>
                  {page.slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </option>
              ))}
            </select>
          )}
          {activeTab === "events" && (
            <select
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              className="bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
              data-testid="event-filter"
            >
              <option value="all">All Events</option>
              {events.map(event => (
                <option key={event.id} value={event.id}>
                  {event.title} ({bookingsByEvent[event.id]?.count || 0} bookings)
                </option>
              ))}
            </select>
          )}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
            data-testid="status-filter"
          >
            <option value="all">All Confirmed</option>
            <option value="paid">Paid</option>
          </select>
        </div>

        {/* Event Summary Cards (only for events tab) */}
        {activeTab === "events" && selectedEvent === "all" && Object.keys(bookingsByEvent).length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(bookingsByEvent).map(([eventId, data]) => (
              <div 
                key={eventId}
                onClick={() => setSelectedEvent(eventId)}
                className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.15)] p-4 cursor-pointer hover:border-[#D4AF37] transition-colors"
              >
                <h3 className="text-[#F5F5F0] font-medium mb-2 truncate">{data.event_title}</h3>
                <div className="flex justify-between text-sm">
                  <span className="text-[#A1A1AA]">{data.count} bookings</span>
                  <span className="text-[#D4AF37]">C${data.revenue.toLocaleString()}</span>
                </div>
                <div className="text-xs text-[#a1a1aa] mt-1">{data.guests} guests</div>
              </div>
            ))}
          </div>
        )}

        {/* Bookings Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="text-center py-20 bg-[#121212] border border-[rgba(212,175,55,0.15)]">
            <Calendar className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
            <h3 className="text-xl font-['Playfair_Display'] text-[#F5F5F0] mb-2">No Bookings Found</h3>
            <p className="text-[#A1A1AA]">No bookings match your search criteria</p>
          </div>
        ) : (
          <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[rgba(212,175,55,0.15)] bg-[#0F0F0F]">
                    {activeTab === "landing" && (
                      <th className="py-4 px-4 w-12">
                        <button
                          onClick={toggleSelectAll}
                          className="p-1 text-[#A1A1AA] hover:text-[#D4AF37]"
                        >
                          {selectedBookings.length === filteredBookings.length && filteredBookings.length > 0 ? (
                            <CheckSquare className="w-5 h-5 text-[#D4AF37]" />
                          ) : (
                            <Square className="w-5 h-5" />
                          )}
                        </button>
                      </th>
                    )}
                    <th className="text-left py-4 px-6 text-[#A1A1AA] text-sm font-medium">Customer</th>
                    <th className="text-left py-4 px-6 text-[#A1A1AA] text-sm font-medium">
                      {activeTab === "landing" ? "Event / Ticket" : activeTab === "events" ? "Event / Ticket" : "Package / Date"}
                    </th>
                    <th className="text-left py-4 px-6 text-[#A1A1AA] text-sm font-medium">
                      {activeTab === "experiences" ? "Guests" : "Qty"}
                    </th>
                    <th className="text-left py-4 px-6 text-[#A1A1AA] text-sm font-medium">Amount</th>
                    {activeTab === "landing" && (
                      <th className="text-left py-4 px-6 text-[#A1A1AA] text-sm font-medium">Source</th>
                    )}
                    <th className="text-left py-4 px-6 text-[#A1A1AA] text-sm font-medium">Status</th>
                    <th className="text-left py-4 px-6 text-[#A1A1AA] text-sm font-medium">Date</th>
                    {activeTab === "landing" && (
                      <th className="text-left py-4 px-6 text-[#A1A1AA] text-sm font-medium">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking) => (
                    <motion.tr 
                      key={booking.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className={`border-b border-[rgba(212,175,55,0.1)] hover:bg-[rgba(212,175,55,0.02)] ${
                        selectedBookings.includes(booking.id) ? 'bg-[rgba(212,175,55,0.05)]' : ''
                      }`}
                    >
                      {activeTab === "landing" && (
                        <td className="py-4 px-4">
                          <button
                            onClick={() => toggleBookingSelection(booking.id)}
                            className="p-1 text-[#A1A1AA] hover:text-[#D4AF37]"
                          >
                            {selectedBookings.includes(booking.id) ? (
                              <CheckSquare className="w-5 h-5 text-[#D4AF37]" />
                            ) : (
                              <Square className="w-5 h-5" />
                            )}
                          </button>
                        </td>
                      )}
                      <td className="py-4 px-6">
                        <p className="text-[#F5F5F0] font-medium">{booking.name}</p>
                        <p className="text-[#a1a1aa] text-sm">{booking.email}</p>
                        <p className="text-[#a1a1aa] text-sm">{booking.phone}</p>
                        {booking.booking_code && (
                          <p className="text-[#D4AF37] text-xs font-mono mt-1">{booking.booking_code}</p>
                        )}
                        {booking.id && (
                          <p className="text-[#22C55E] text-xs font-mono mt-0.5" data-testid="booking-ticket-number" title="Ticket # on PDF">
                            Ticket #{(booking.id || "").substring(0, 8).toUpperCase()}
                          </p>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        {activeTab === "landing" ? (
                          <>
                            <p className="text-[#F5F5F0]">{booking.page_title || booking.page_slug}</p>
                            <p className="text-[#D4AF37] text-sm">{booking.ticket_type}</p>
                            {booking.payment_reference && (
                              <p className="text-[#6B7280] text-xs font-mono truncate max-w-[150px]" title={booking.payment_reference}>
                                Ref: {booking.payment_reference?.slice(0, 15)}...
                              </p>
                            )}
                          </>
                        ) : activeTab === "events" ? (
                          <>
                            <p className="text-[#F5F5F0]">{booking.event_title}</p>
                            <p className="text-[#D4AF37] text-sm">{booking.ticket_type}</p>
                          </>
                        ) : (
                          <>
                            <p className="text-[#F5F5F0]">{booking.package_name}</p>
                            <p className="text-[#D4AF37] text-sm">{booking.event_date}</p>
                          </>
                        )}
                      </td>
                      <td className="py-4 px-6 text-[#F5F5F0]">
                        {activeTab === "experiences" ? booking.guest_count : booking.quantity}
                      </td>
                      <td className="py-4 px-6 text-[#F5F5F0] font-medium">
                        C${booking.total_amount?.toFixed(2)}
                      </td>
                      {activeTab === "landing" && (
                        <td className="py-4 px-6">
                          <span className={`px-2 py-1 text-xs rounded ${
                            booking.source === 'manual' 
                              ? 'bg-purple-900/30 text-purple-400' 
                              : 'bg-blue-900/30 text-blue-400'
                          }`}>
                            {booking.landing_page_display || booking.page_slug?.toUpperCase().replace(/-/g, ' ') || 'N/A'}
                          </span>
                        </td>
                      )}
                      <td className="py-4 px-6">
                        <span className={`px-3 py-1 text-xs uppercase tracking-wider ${
                          booking.payment_status === 'paid' 
                            ? 'bg-green-900/30 text-green-500' 
                            : booking.payment_status === 'pending'
                            ? 'bg-yellow-900/30 text-yellow-500'
                            : 'bg-red-900/30 text-red-500'
                        }`}>
                          {booking.payment_status}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-[#A1A1AA] text-sm">
                        {formatDate(booking.created_at)}
                      </td>
                      {activeTab === "landing" && (
                        <td className="py-4 px-6">
                          <div className="flex gap-2">
                            <button
                              onClick={() => setViewingEmail(booking)}
                              className="p-2 bg-purple-900/30 text-purple-400 hover:bg-purple-900/50 rounded transition-colors"
                              title="View email sent to guest"
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setEditingBooking(booking)}
                              className="p-2 bg-blue-900/30 text-blue-400 hover:bg-blue-900/50 rounded transition-colors"
                              title="Edit booking"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            {booking.payment_status !== 'paid' && (
                              <button
                                onClick={() => confirmBooking(booking.id)}
                                className="p-2 bg-green-900/30 text-green-500 hover:bg-green-900/50 rounded transition-colors"
                                title="Confirm payment & send ticket"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => resendEmail(booking.id)}
                              className="p-2 bg-[rgba(212,175,55,0.1)] text-[#D4AF37] hover:bg-[rgba(212,175,55,0.2)] rounded transition-colors"
                              title="Resend confirmation email"
                            >
                              <Mail className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeletingBooking(booking)}
                              className="p-2 bg-red-900/30 text-red-500 hover:bg-red-900/50 rounded transition-colors"
                              title="Delete booking"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Edit Booking Modal */}
      {editingBooking && (
        <EditBookingModal
          booking={editingBooking}
          onClose={() => setEditingBooking(null)}
          onSave={fetchData}
        />
      )}

      {/* Email Preview Modal */}
      {viewingEmail && (
        <EmailPreviewModal
          booking={viewingEmail}
          landingPages={landingPages}
          onClose={() => setViewingEmail(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingBooking && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#0F0F0F] border border-red-900/50 w-full max-w-md p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-900/30 flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#F5F5F0]">Delete Booking</h3>
                <p className="text-[#A1A1AA] text-sm">This action cannot be undone</p>
              </div>
            </div>

            <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-4 mb-6">
              <p className="text-[#F5F5F0] font-medium">{deletingBooking.name}</p>
              <p className="text-[#A1A1AA] text-sm">{deletingBooking.email}</p>
              <p className="text-[#D4AF37] text-sm mt-2">{deletingBooking.ticket_type} • C${deletingBooking.total_amount?.toFixed(2)}</p>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setDeletingBooking(null)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteBooking(deletingBooking.id)}
                className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium transition-colors"
              >
                Delete Booking
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDelete && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#0F0F0F] border border-red-900/50 w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
          >
            <div className="p-6 border-b border-[rgba(212,175,55,0.15)]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-red-900/30 flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#F5F5F0]">Bulk Delete Bookings</h3>
                  <p className="text-[#A1A1AA] text-sm">Uncheck any bookings you want to keep</p>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {filteredBookings
                .filter(b => selectedBookings.includes(b.id))
                .map(booking => (
                  <div 
                    key={booking.id}
                    className="flex items-center gap-3 p-3 bg-[#121212] border border-[rgba(212,175,55,0.15)] rounded"
                  >
                    <button
                      onClick={() => toggleBookingSelection(booking.id)}
                      className="text-[#D4AF37]"
                    >
                      <CheckSquare className="w-5 h-5" />
                    </button>
                    <div className="flex-1">
                      <p className="text-[#F5F5F0] font-medium">{booking.name}</p>
                      <p className="text-[#A1A1AA] text-sm">{booking.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[#D4AF37] text-sm">{booking.ticket_type}</p>
                      <p className="text-[#A1A1AA] text-xs">C${booking.total_amount?.toFixed(2)}</p>
                    </div>
                  </div>
                ))}
            </div>

            <div className="p-6 border-t border-[rgba(212,175,55,0.15)]">
              <p className="text-sm text-[#A1A1AA] mb-4">
                {selectedBookings.length} booking(s) will be deleted. This action cannot be undone.
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => setShowBulkDelete(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={bulkDeleteBookings}
                  disabled={bulkDeleting || selectedBookings.length === 0}
                  className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium transition-colors disabled:opacity-50"
                >
                  {bulkDeleting ? "Deleting..." : `Delete ${selectedBookings.length} Bookings`}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Add Manual Booking Modal */}
      {showAddManual && (
        <AddManualBookingModal
          mainEvents={mainEvents}
          landingPages={landingPages}
          onClose={() => setShowAddManual(false)}
          onSave={fetchData}
        />
      )}
    </AdminLayout>
  );
};

export default AdminBookingsPage;
